(ns ncr.nsql.simple
  (:require
   [clojure.string :as str]
   [clojure.core.match :refer [match]]
   [taoensso.encore :as e]
   [globus.core :as glob]
   [tick.core :as t]
   [tick.locale-en-us]

   [ncr.nsql.context :as context]
   [ncr.nsql.locale :as locale]))

(def ^:private types
  (-> (make-hierarchy)
    (derive :timestamp :date)
    (derive :date :date-like)
    (derive :date/datetime :date-like)
    (derive :date/year :date-like)
    (derive :date/year-month :date-like)
    (derive :either-texts :text)
    (derive :either/decimal :decimal)))

(defmulti parse-arg
  (fn [prop arg] (second prop))
  :hierarchy #'types)

(defmethod parse-arg :default [_ arg] [:string arg])

(defn- const-check [propname check const]
  (let [[tag & _ :as prop] (context/lookup-ident (keyword propname))]
    (if (not= :column tag)
      [:error {:code :bad-property :details {:obj propname}}]
      [:call check prop (context/lookup-ident const)])))

(defn- parse-date [arg]
  (let [l (:locale context/*context*)]
    (or
      (reduce
        #(some->> (locale/parse-date %2 arg l) (conj [%2]) reduced)
        nil
        [:date :date/year-month :date/year])
      [:error {:code :bad-date :details {:obj arg}}])))

(defmethod parse-arg :date [_ arg]
  (try
    (let [exp (glob/explode arg)]
      (if (= 1 (count exp))
        (parse-date (first exp))
        (into [:date/set] (map parse-date exp))))
    (catch #?(:clj Exception :cljs js/Error) e
      [:error {:code :bad-glob :details {:obj arg}}])))

(defmethod parse-arg :date/interval [_ arg]
  (parse-arg [nil :date] arg))

(defmethod parse-arg :decimal [_ arg]
  (or
    (some->> (locale/parse-decimal arg) (conj [:decimal]))
    [:error {:code :bad-decimal :details {:obj arg}}]))

(defmethod parse-arg :boolean [_ arg]
  (case (keyword (str/lower-case arg))
    :true [:boolean true]
    :yes [:boolean true]
    :1es [:boolean true]
    :false [:boolean false]
    :no [:boolean false]
    :0 [:boolean false]
    [:error {:code :bad-boolean :details {:obj arg}}]))

(defmulti clause
  (fn [prop method & args] (into [(second prop) method] (map first args)))
  :hierarchy #'types)

#_(ns-unmap *ns* 'clause)

(defn- funcall [prop pred & args]
  (let [args (for [arg args] (parse-arg prop arg))]
    (if-let [e (some #(match % [:error _] % _ false) args)]
      e
      (apply clause prop pred args))))

(defn funcall->ir [[tag & args :as form]]
  {:pre (= tag :simple-funcall)}

  (let [h (into {} (map (juxt first rest) args))
        negate (contains? h :simple-not)
        [noun pred] (map #(apply keyword (rest %)) (:simple-noun-pred h))
        [tag :as prop] (context/lookup-ident noun)]
    (cond->>
      (if tag
        (if (not= tag :column)
          [:error {:code :bad-property :details {:tag tag :obj noun}}]
          (apply funcall prop (or pred :matches) (:simple-args h)))
        (if pred
          [:error {:code :bad-property :details {:pred pred :obj noun}}]
          (apply funcall nil noun (:simple-args h))))
      negate (conj [:call :not]))))

(defmethod clause :default [prop method & args]
  [:error
   {:code :unknown-method
    :details {:obj (peek prop) :method method :args (map pr-str args)}}])

(defmethod clause [:date :before :date-like] [prop _ [_ arg]]
  [:call :< prop [:immediate :date/datetime (t/beginning arg)]])

(defmethod clause [:date :before :date/set] [prop _ [_ & args]]
  (let [arg (apply t/min (map (comp t/beginning second) args))]
    [:call :< prop [:immediate :date/datetime arg]]))

(defmethod clause [nil :before :string] [prop _ [_ arg]]
  (funcall (context/lookup-ident :date) :before arg))

(defmethod clause [:date :after :date-like] [prop _ [_ arg]]
  [:call :>= prop [:immediate :date/datetime (t/end arg)]])

(defmethod clause [:date :after :date/set] [prop _ [_ args]]
  (let [arg (apply t/max (map (comp t/end second) args))]
    [:call :>= prop [:immediate :date/datetime arg]]))

(defmethod clause [nil :after :string] [prop _ [_ arg]]
  (funcall (context/lookup-ident :date) :after arg))

(defmethod clause [:date :matches :date] [prop _ [arg-t arg]]
  [:call := prop [:immediate arg-t arg]])

(defmethod clause [:date :matches :date-like] [prop _ [_ arg]]
  [:call :and
   [:call :>= prop [:immediate :date/datetime (t/beginning arg)]]
   [:call :< prop [:immediate :date/datetime (t/end arg)]]])

(defmethod clause [:date :matches :date/set] [prop meth [_ & args]]
  (reduce (fn [s x] [:call :or s x])
    (map #(clause prop meth %) args)))

(defmethod clause [:date/interval :matches :date-like] [prop _ [_ arg]]
  (let [[_ _ [start-col end-col]] prop]
    [:call :and
     [:call :<=
      [:column :date start-col]
      [:immediate :date/datetime (t/end arg)]]
     [:call :or
      [:call :is [:column :date end-col] [:immediate :null nil]]
      [:call :>
       [:column :date end-col]
       [:immediate :date/datetime (t/beginning arg)]]]]))

(defmethod clause [:decimal :matches :decimal] [prop _ [_ arg]]
  [:call := prop [:immediate :decimal arg]])

(defmethod clause [:decimal :greater :decimal] [prop _ [_ arg]]
  [:call :> prop [:immediate :decimal arg]])

(defmethod clause [:boolean :matches :boolean] [prop _ [_ arg]]
  [:call := prop [:immediate :boolean arg]])

(defmethod clause [nil :greater :string] [prop _ [_ arg]]
  (funcall (context/lookup-ident :value) :greater arg))

(defmethod clause [:decimal :less :decimal] [prop _ [_ arg]]
  [:call :< prop [:immediate :decimal arg]])

(defmethod clause [nil :less :string] [prop _ [_ arg]]
  (funcall (context/lookup-ident :value) :less arg))

(defmethod clause [:decimal :around :decimal] [prop _ [_ arg]]
  (let [delta (* 0.1 arg)]
    [:call :and
     [:call :<= prop [:immediate :decimal (+ arg delta)]]
     [:call :>= prop [:immediate :decimal (- arg delta)]]]))

(defmethod clause [nil :around :string] [prop _ [_ arg]]
  (funcall (context/lookup-ident :value) :around arg))

(defn- glob-or-equals [prop arg]
  (try
    (let [method (if (glob/glob? arg) :glob :=)]
      [:call method prop [:immediate :string arg]])
    (catch #?(:clj Exception :cljs js/Error) e
      [:error {:code :bad-glob :details {:obj arg}}])))

(defmethod clause [:folder :matches :string] [prop _ [_ arg]]
  (glob-or-equals prop arg))

(defmethod clause [:label :matches :string] [prop _ [_ arg]]
  (glob-or-equals prop arg))

(defmethod clause [:labels :matches :string] [prop _ [_ arg]]
  (glob-or-equals prop arg))

(defmethod clause [:user :matches :string] [prop _ [_ arg]]
  (glob-or-equals prop arg))

(def ^:private MAGIC-PLACES
  ["incoming" "accepted" "revoked"])

(defmethod clause [nil :in :string] [_ _ [_ s :as arg]]
  (e/cond
    (= s "incoming")
    (const-check :folder :is-not :null)

    (= s "accepted")
    (const-check :folder :is :null)

    (= s "revoked")
    (const-check :revoked :is-not :null)

    :let [f-clause (clause (context/lookup-ident :folder) :matches arg)
          matches (when (glob/glob? s)
                    (glob/glob s MAGIC-PLACES {:ignorecase true}))]

    (seq matches)
    (reduce (fn [s x] [:call :or s x])
      f-clause
      (for [m matches]
        (clause nil :in [:string m])))

    f-clause))

(defmethod clause [nil :label :string] [_ _ arg]
  (clause (context/lookup-ident :labels) :matches arg))

(defmethod clause [nil :has :string] [_ _ [_ arg]]
  (if (glob/glob? arg)
    (let [cols
          (->> context/*context*
            :env
            keys
            (mapcat
              #(let [[nspace nm :as full] ((juxt namespace name) %)
                     fullname (cond->> nm nspace (str nspace "."))]
                 (cond-> [fullname]
                   (= nspace "common") (conj nm))))
            (glob/glob arg))]
      (if (seq cols)
        (->> cols
          (map #(const-check (keyword %) :is-not :null))
          (reduce (fn [s x] [:call :or s x])))
        [:immediate :boolean false]))
    (const-check (str/replace-first arg "." "/") :is-not :null)))

(defmethod clause [nil :status :string] [_ _ [_ arg]]
  (condp = arg
    "promoted" (const-check :folder :is :null)
    "inbox" (const-check :folder :is-not :null)
    [:error {:code :bad-status :details {:obj arg}}]))

(defmethod clause [:text :matches :string] [prop _ [_ arg]]
  [:call :fts prop [:immediate :string arg]])
